import { useEffect, useState } from "react";
import { API_BASE } from "./config";
import { solveImageChallenge } from "./imageChallenge";

const SESSION_KEY = "backup.imageUploadSession";
const COOLDOWN_KEY = "backup.imageUploadCooldown";
let sessionPromise;
let memorySession;
const read = (key) => { try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; } };
const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch {} };
let cooldownUntil = 0;
const remaining = () => Math.max(0, Math.ceil((Math.max(cooldownUntil, Number(read(COOLDOWN_KEY)) || 0) - Date.now()) / 1000));

async function timedFetch(url, options) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 60000);
  try { return await fetch(url, { ...options, signal: controller.signal }); }
  finally { clearTimeout(timeout); }
}

const saveCooldown = (response, data) => {
  const seconds = Number(data?.retry_after || response.headers?.get("Retry-After"));
  if (seconds > 0 && [429, 503].includes(response.status)) {
    cooldownUntil = Date.now() + seconds * 1000;
    write(COOLDOWN_KEY, cooldownUntil);
    window.dispatchEvent(new Event("image-upload-cooldown"));
  }
};

export const assertImageUploadAllowed = () => {
  const seconds = remaining();
  if (seconds) throw new Error(`Please wait ${seconds} seconds before checking another image.`);
};

export const useImageUploadCooldown = () => {
  const [seconds, setSeconds] = useState(remaining);
  useEffect(() => {
    const update = () => setSeconds(remaining());
    window.addEventListener("image-upload-cooldown", update);
    window.addEventListener("storage", update);
    const timer = setInterval(update, 1000);
    return () => {
      clearInterval(timer);
      window.removeEventListener("image-upload-cooldown", update);
      window.removeEventListener("storage", update);
    };
  }, []);
  return seconds;
};

async function sessionToken() {
  const saved = memorySession || read(SESSION_KEY);
  if (saved?.token && saved.expires > Date.now()) return saved.token;
  if (!sessionPromise) {
    sessionPromise = (async () => {
      const response = await timedFetch(`${API_BASE}/crew/image-upload-session/`, { method: "POST" });
      const data = await response.json();
      if (!response.ok || !data.token) {
        saveCooldown(response, data);
        throw Object.assign(new Error(data.detail || "Could not start the image check. Please try again."), {
          status: response.status, data,
        });
      }
      memorySession = { token: data.token, expires: Date.now() + (Number(data.expires_in) - 60) * 1000 };
      write(SESSION_KEY, memorySession);
      return data.token;
    })().finally(() => { sessionPromise = null; });
  }
  return sessionPromise;
}

// Works with fetch or the existing authenticated transport. The backend is the
// authority; persisted cooldowns only make the UI clearer and reduce retries.
export async function withImageProtection(send, options = {}) {
  let token = await sessionToken();
  let challengeToken;
  let renewed = false;
  let challenged = false;
  for (;;) {
    const headers = { ...options.headers, "X-Image-Session": token };
    if (challengeToken) headers["X-Image-Challenge"] = challengeToken;
    const response = await send({ ...options, headers });
    if (response.ok) return response;
    const data = await response.clone().json().catch(() => ({}));
    if (data.code === "image_session_expired" && !renewed) {
      renewed = true;
      memorySession = null;
      write(SESSION_KEY, null);
      token = await sessionToken();
      continue;
    }
    if (data.code === "image_challenge_required" && data.challenge && !challenged) {
      challenged = true;
      try {
        challengeToken = await solveImageChallenge(data.challenge);
      } catch (error) {
        throw Object.assign(error, { status: 403, data: { code: "image_challenge_required", detail: error.message } });
      }
      continue;
    }
    saveCooldown(response, data);
    return response;
  }
}

export const imageValidationFetch = (url, options) => withImageProtection((next) => timedFetch(url, next), options);
